<template>
  <div class="prompt-assist-field" @keydown.esc="closePanel">
    <el-input
      ref="inputRef"
      :model-value="modelValue"
      type="textarea"
      :rows="rows"
      :maxlength="maxlength"
      :show-word-limit="showWordLimit"
      :placeholder="placeholder"
      @update:model-value="handleValueUpdate"
      @input="handleTextInput"
    />

    <div v-if="mentionHintText" class="prompt-assist-field__hint">{{ mentionHintText }}</div>

    <transition name="prompt-assist-field-fade">
      <div v-if="mentionPanelVisible" class="prompt-assist-field__mentions">
        <div class="prompt-assist-field__mentions-head">
          <strong>引用官方资产</strong>
          <span>输入 `@` 后可插入角色、场景、道具的唯一名称</span>
        </div>
        <button
          v-for="(item, index) in filteredMentionItems"
          :key="item.key"
          type="button"
          class="prompt-assist-field__mention-item"
          :class="{ 'prompt-assist-field__mention-item--active': index === activeMentionIndex }"
          @mousedown.prevent="applyMentionItem(item)"
          @mouseenter="activeMentionIndex = index"
        >
          <div class="prompt-assist-field__mention-title-row">
            <strong>@{{ item.name }}</strong>
            <span>{{ item.typeLabel }}</span>
          </div>
          <small>{{ item.description }}</small>
        </button>
        <div v-if="mentionCreateVisible" class="prompt-assist-field__mention-empty">
          <strong>还没有找到“{{ mentionCreateName }}”</strong>
          <p>可以先把它加入当前项目的官方道具资产，保存后当前 `@{{ mentionCreateName }}` 就会生效。</p>
          <button type="button" class="prompt-assist-field__mention-create" @mousedown.prevent="handleCreateMentionItem">
            新增道具资产 @{{ mentionCreateName }}
          </button>
        </div>
      </div>
    </transition>

    <transition name="prompt-assist-field-fade">
      <button
        v-if="!panelOpen"
        type="button"
        class="prompt-assist-field__fab"
        @click="togglePanel"
      >
        {{ triggerLabel }}
      </button>
    </transition>

    <transition name="prompt-assist-field-fade">
      <div v-if="panelOpen" class="prompt-assist-field__panel">
        <div class="prompt-assist-field__panel-head">
          <div>
            <strong>{{ assistantTitle || `${fieldLabel}助手` }}</strong>
            <p>{{ assistantDescriptionText }}</p>
          </div>
          <button type="button" class="prompt-assist-field__close" @click="closePanel">关闭</button>
        </div>

        <div class="prompt-assist-field__section">
          <span>当前文本</span>
          <p>{{ currentPreviewText }}</p>
        </div>

        <div class="prompt-assist-field__section">
          <span>补充指令</span>
          <el-input
            v-model="instruction"
            type="textarea"
            :rows="4"
            maxlength="2000"
            show-word-limit
            :placeholder="assistantPlaceholder || defaultInstructionPlaceholder"
          />
        </div>

        <div v-if="generatedText" class="prompt-assist-field__section">
          <span>生成结果</span>
          <el-input
            :model-value="generatedText"
            type="textarea"
            :rows="6"
            readonly
          />
        </div>

        <p v-if="errorMessage" class="prompt-assist-field__error">{{ errorMessage }}</p>

        <div class="prompt-assist-field__actions">
          <el-button plain :disabled="loading" @click="closePanel">稍后再说</el-button>
          <GenerationModelSplitButton
            :action-label="generatedText ? '重新生成' : '生成建议'"
            :options="textModelOptions"
            :model-value="selectedTextModelId"
            :loading="loading"
            @update:model-value="handleUpdateTextModel"
            @trigger="handleGenerate"
          />
          <el-button type="success" :disabled="!generatedText || loading" @click="applyGeneratedText">
            使用结果
          </el-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import GenerationModelSplitButton from '@/components/GenerationModelSplitButton.vue'
import { optimizeWorkflowPrompt } from '@/api/workflow'
import { useModelCatalogStore } from '@/stores/modelCatalog'
import { resolveGenerationModelSelection, setGenerationModelSelection } from '@/utils/generationModelSelection'

interface PromptMentionItem {
  key: string
  name: string
  typeLabel: string
  description: string
}

type TextareaInputRef = {
  textarea?: HTMLTextAreaElement | null
}

const props = withDefaults(defineProps<{
  modelValue: string
  projectId: number
  fieldLabel: string
  placeholder?: string
  rows?: number
  maxlength?: number
  showWordLimit?: boolean
  assistantTitle?: string
  assistantDescription?: string
  assistantPlaceholder?: string
  stage?: string
  sceneNumber?: number
  assetType?: string
  assetName?: string
  purpose?: string
  mentionItems?: PromptMentionItem[]
  allowCreateMention?: boolean
}>(), {
  placeholder: '',
  rows: 5,
  maxlength: 1500,
  showWordLimit: true,
  assistantTitle: '',
  assistantDescription: '',
  assistantPlaceholder: '',
  stage: 'storyboard',
  sceneNumber: undefined,
  assetType: '',
  assetName: '',
  purpose: 'prompt',
  mentionItems: () => [],
  allowCreateMention: false,
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
  (event: 'input'): void
  (event: 'create-mention-item', payload: { name: string; sceneNumber?: number }): void
}>()

const panelOpen = ref(false)
const instruction = ref('')
const generatedText = ref('')
const loading = ref(false)
const errorMessage = ref('')
const selectedTextModelId = ref('')
const inputRef = ref<TextareaInputRef | null>(null)
const mentionQuery = ref('')
const mentionRange = ref<{ start: number; end: number } | null>(null)
const activeMentionIndex = ref(0)

let textareaEventCleanup: (() => void) | null = null
const modelCatalogStore = useModelCatalogStore()

const textModelOptions = computed(() => modelCatalogStore.textModelsForOperation('prompt_assist'))

function handleUpdateTextModel(value: string) {
  selectedTextModelId.value = value
  setGenerationModelSelection('text', 'prompt_assist', value)
}

const assistantDescriptionText = computed(() => (
  props.assistantDescription
  || '直接描述你想补强的点，系统会基于当前文本整理出一版更适合直接使用的结果。'
))

const triggerLabel = computed(() => {
  if (props.purpose === 'consistency_prompt') {
    return '补充设定'
  }
  if (props.purpose === 'image_prompt' || props.purpose === 'video_prompt') {
    return 'AI 补强'
  }
  return 'AI 优化'
})

const mentionHintText = computed(() => {
  if (!props.mentionItems.length) {
    return ''
  }

  const examples = props.mentionItems.slice(0, 3).map((item) => `@${item.name}`).join('、')
  return examples
    ? `输入 @ 可引用官方资产，例如 ${examples}；生成时会自动带入对应设定与参考图。`
    : '输入 @ 可引用官方资产，生成时会自动带入对应设定与参考图。'
})

const mentionCreateName = computed(() => String(mentionQuery.value || '').trim())

const mentionCreateVisible = computed(() => (
  props.allowCreateMention
  && Boolean(mentionRange.value)
  && Boolean(mentionCreateName.value)
  && filteredMentionItems.value.length === 0
))

const currentPreviewText = computed(() => {
  const text = String(props.modelValue || '').trim()
  if (!text) {
    return '当前还是空白，你可以直接描述希望补充的画面、动作、风格或节奏。'
  }
  return text.length > 220 ? `${text.slice(0, 220)}...` : text
})

const defaultInstructionPlaceholder = computed(() => {
  if (props.purpose === 'video_prompt') {
    return '例如：强化镜头运动感，突出主角情绪，保留现有场景信息，声音氛围更沉浸。'
  }
  if (props.purpose === 'image_prompt') {
    return '例如：让光影更电影化，突出主体层次和环境质感，保留当前构图关系。'
  }
  if (props.purpose === 'consistency_prompt') {
    return '例如：补充固定服装、颜色、材质和禁变项，让后续所有镜头更稳定。'
  }
  return '例如：在保留当前意思的前提下，让文本更具体、更清晰、更适合生成。'
})

const filteredMentionItems = computed(() => {
  if (!mentionRange.value) {
    return [] as PromptMentionItem[]
  }

  const query = String(mentionQuery.value || '').trim().toLowerCase()
  const sorted = [...props.mentionItems].sort((left, right) => {
    const leftName = left.name.toLowerCase()
    const rightName = right.name.toLowerCase()
    const leftStartsWith = query ? leftName.startsWith(query) : false
    const rightStartsWith = query ? rightName.startsWith(query) : false
    if (leftStartsWith !== rightStartsWith) {
      return leftStartsWith ? -1 : 1
    }
    const leftIncludes = query ? leftName.includes(query) : true
    const rightIncludes = query ? rightName.includes(query) : true
    if (leftIncludes !== rightIncludes) {
      return leftIncludes ? -1 : 1
    }
    return left.name.localeCompare(right.name, 'zh-CN')
  })

  return sorted
    .filter((item) => {
      if (!query) {
        return true
      }
      const name = item.name.toLowerCase()
      const typeLabel = item.typeLabel.toLowerCase()
      const description = item.description.toLowerCase()
      return name.includes(query) || typeLabel.includes(query) || description.includes(query)
    })
    .slice(0, 8)
})

const mentionPanelVisible = computed(() => Boolean(mentionRange.value) && (filteredMentionItems.value.length > 0 || mentionCreateVisible.value))

function handleValueUpdate(value: string) {
  emit('update:modelValue', value)
  void nextTick(syncMentionState)
}

function handleTextInput() {
  emit('input')
  void nextTick(syncMentionState)
}

function togglePanel() {
  panelOpen.value = !panelOpen.value
  errorMessage.value = ''
}

function closePanel() {
  panelOpen.value = false
  errorMessage.value = ''
}

function closeMentionPanel() {
  mentionQuery.value = ''
  mentionRange.value = null
  activeMentionIndex.value = 0
}

function getTextareaElement() {
  return inputRef.value?.textarea || null
}

function syncMentionState() {
  if (!props.mentionItems.length) {
    closeMentionPanel()
    return
  }

  const textarea = getTextareaElement()
  const value = String(props.modelValue || '')
  if (!textarea) {
    closeMentionPanel()
    return
  }

  const cursor = textarea.selectionStart ?? value.length
  const beforeCursor = value.slice(0, cursor)
  const match = beforeCursor.match(/(?:^|[^\w@])@([^\s@，。；,.;:：!?！？、]*)$/)
  if (!match) {
    closeMentionPanel()
    return
  }

  const query = String(match[1] || '')
  mentionQuery.value = query
  mentionRange.value = {
    start: cursor - (`@${query}`).length,
    end: cursor,
  }

  if (activeMentionIndex.value >= filteredMentionItems.value.length) {
    activeMentionIndex.value = 0
  }
}

function handleMentionKeydown(event: KeyboardEvent) {
  if (!mentionPanelVisible.value) {
    return
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    activeMentionIndex.value = (activeMentionIndex.value + 1) % filteredMentionItems.value.length
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    activeMentionIndex.value = (activeMentionIndex.value - 1 + filteredMentionItems.value.length) % filteredMentionItems.value.length
    return
  }

  if (event.key === 'Enter' || event.key === 'Tab') {
    const item = filteredMentionItems.value[activeMentionIndex.value]
    if (item) {
      event.preventDefault()
      applyMentionItem(item)
      return
    }
    if (mentionCreateVisible.value) {
      event.preventDefault()
      handleCreateMentionItem()
    }
    return
  }

  if (event.key === 'Escape') {
    closeMentionPanel()
  }
}

function applyMentionItem(item: PromptMentionItem) {
  const range = mentionRange.value
  if (!range) {
    return
  }

  const currentValue = String(props.modelValue || '')
  const insertText = `@${item.name} `
  const nextValue = `${currentValue.slice(0, range.start)}${insertText}${currentValue.slice(range.end)}`
  const nextCursor = range.start + insertText.length

  emit('update:modelValue', nextValue)
  emit('input')
  closeMentionPanel()

  void nextTick(() => {
    const textarea = getTextareaElement()
    if (!textarea) {
      return
    }
    textarea.focus()
    textarea.setSelectionRange(nextCursor, nextCursor)
  })
}

function handleCreateMentionItem() {
  const name = mentionCreateName.value
  if (!name) {
    return
  }
  emit('create-mention-item', {
    name,
    sceneNumber: props.sceneNumber,
  })
  closeMentionPanel()
}

async function bindTextareaEvents() {
  await nextTick()
  const textarea = getTextareaElement()

  textareaEventCleanup?.()
  textareaEventCleanup = null

  if (!textarea) {
    return
  }

  const syncHandler = () => syncMentionState()
  const keydownHandler = (event: Event) => handleMentionKeydown(event as KeyboardEvent)

  textarea.addEventListener('click', syncHandler)
  textarea.addEventListener('focus', syncHandler)
  textarea.addEventListener('keyup', syncHandler)
  textarea.addEventListener('keydown', keydownHandler)

  textareaEventCleanup = () => {
    textarea.removeEventListener('click', syncHandler)
    textarea.removeEventListener('focus', syncHandler)
    textarea.removeEventListener('keyup', syncHandler)
    textarea.removeEventListener('keydown', keydownHandler)
  }
}

async function handleGenerate() {
  if (!instruction.value.trim()) {
    errorMessage.value = '先输入你希望系统补强或调整的点。'
    return
  }

  loading.value = true
  errorMessage.value = ''
  try {
    const response = await optimizeWorkflowPrompt({
      project_id: props.projectId,
      field_label: props.fieldLabel,
      current_text: props.modelValue || '',
      instruction: instruction.value.trim(),
      stage: props.stage,
      scene_number: props.sceneNumber,
      asset_type: props.assetType || undefined,
      asset_name: props.assetName || undefined,
      purpose: props.purpose || 'prompt',
      text_model_id: selectedTextModelId.value || undefined,
      operation_type: 'prompt_assist',
    })
    generatedText.value = String(response.optimized_text || '').trim()
    if (!generatedText.value) {
      errorMessage.value = '系统没有返回可用结果，请换一种说法再试。'
    }
  } catch (error: any) {
    errorMessage.value = error?.response?.data?.detail || error?.message || '智能提示词生成失败'
  } finally {
    loading.value = false
  }
}

function applyGeneratedText() {
  if (!generatedText.value.trim()) {
    return
  }
  emit('update:modelValue', generatedText.value)
  emit('input')
  closePanel()
}

onMounted(() => {
  void modelCatalogStore.ensureLoaded()
  void bindTextareaEvents()
})

watch(textModelOptions, (options) => {
  if (!options.length) {
    selectedTextModelId.value = ''
    return
  }
  if (!selectedTextModelId.value || !options.some((item) => item.id === selectedTextModelId.value)) {
    selectedTextModelId.value = resolveGenerationModelSelection('text', 'prompt_assist', options)
  }
}, { immediate: true })

watch(() => props.mentionItems, () => {
  void nextTick(syncMentionState)
}, { deep: true })

watch(() => props.modelValue, () => {
  void nextTick(syncMentionState)
})

watch(inputRef, () => {
  void bindTextareaEvents()
})

onBeforeUnmount(() => {
  textareaEventCleanup?.()
})
</script>

<style scoped>
.prompt-assist-field {
  position: relative;
  display: grid;
  gap: 12px;
}

.prompt-assist-field :deep(.el-textarea__inner) {
  padding-bottom: 58px;
}

.prompt-assist-field__hint {
  margin-top: -4px;
  color: #0f766e;
  font-size: 12px;
  line-height: 1.6;
}

.prompt-assist-field__mentions {
  display: grid;
  gap: 10px;
  padding: 14px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(240, 253, 250, 0.98), rgba(236, 253, 245, 0.96));
  border: 1px solid rgba(20, 184, 166, 0.18);
  box-shadow: 0 16px 32px rgba(15, 118, 110, 0.08);
}

.prompt-assist-field__mentions-head {
  display: grid;
  gap: 4px;
}

.prompt-assist-field__mentions-head strong {
  color: #0f172a;
  font-size: 13px;
}

.prompt-assist-field__mentions-head span {
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.prompt-assist-field__mention-item {
  display: grid;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid rgba(20, 184, 166, 0.14);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.8);
  text-align: left;
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.prompt-assist-field__mention-item:hover,
.prompt-assist-field__mention-item--active {
  transform: translateY(-1px);
  border-color: rgba(14, 165, 233, 0.35);
  box-shadow: 0 12px 24px rgba(14, 165, 233, 0.12);
}

.prompt-assist-field__mention-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.prompt-assist-field__mention-title-row strong {
  color: #0f172a;
  font-size: 13px;
}

.prompt-assist-field__mention-title-row span {
  color: #0f766e;
  font-size: 11px;
  font-weight: 700;
}

.prompt-assist-field__mention-item small {
  color: #64748b;
  line-height: 1.5;
}

.prompt-assist-field__mention-empty {
  display: grid;
  gap: 8px;
  padding: 12px 14px;
  border: 1px dashed rgba(20, 184, 166, 0.28);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.72);
}

.prompt-assist-field__mention-empty strong {
  color: #0f172a;
  font-size: 13px;
}

.prompt-assist-field__mention-empty p {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
}

.prompt-assist-field__mention-create {
  justify-self: flex-start;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid rgba(20, 184, 166, 0.22);
  border-radius: 999px;
  background: rgba(15, 118, 110, 0.08);
  color: #0f766e;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.prompt-assist-field__mention-create:hover {
  border-color: rgba(14, 165, 233, 0.28);
  background: rgba(14, 165, 233, 0.08);
}

.prompt-assist-field__fab {
  position: absolute;
  right: 14px;
  bottom: 14px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid rgba(20, 184, 166, 0.22);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #0f766e;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 20px rgba(15, 118, 110, 0.12);
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease, background 0.16s ease;
}

.prompt-assist-field__fab::before {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, #14b8a6, #0ea5e9);
  box-shadow: 0 0 0 4px rgba(45, 212, 191, 0.14);
}

.prompt-assist-field__fab:hover {
  transform: translateY(-1px);
  border-color: rgba(14, 165, 233, 0.3);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 14px 28px rgba(14, 165, 233, 0.16);
}

.prompt-assist-field__panel {
  display: grid;
  gap: 14px;
  padding: 18px;
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98));
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 22px 40px rgba(15, 23, 42, 0.08);
}

.prompt-assist-field__panel-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.prompt-assist-field__panel-head strong {
  color: #0f172a;
}

.prompt-assist-field__panel-head p,
.prompt-assist-field__section p {
  margin: 6px 0 0;
  color: #64748b;
  line-height: 1.7;
}

.prompt-assist-field__close {
  border: 0;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
}

.prompt-assist-field__section {
  display: grid;
  gap: 8px;
}

.prompt-assist-field__section span {
  color: #0f766e;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.prompt-assist-field__error {
  margin: 0;
  color: #dc2626;
  font-size: 13px;
}

.prompt-assist-field__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.prompt-assist-field-fade-enter-active,
.prompt-assist-field-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.prompt-assist-field-fade-enter-from,
.prompt-assist-field-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>